import React, { useState } from 'react';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import OnboardingTutorial from '../onboarding/OnboardingTutorial';
import SuspensionBanner from '../ui/SuspensionBanner';
import AchievementModalManager from '../gamification/AchievementModalManager';
import PWAInstallBanner from '../pwa/PWAInstallBanner';
import { Dumbbell, HelpCircle, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Layout = ({
    children,
    user,
    profile,
    role,
    activeTab,
    onNavigate,
    onLogout,
    isProfileComplete
}) => {
    const [manualTutorialTrigger, setManualTutorialTrigger] = useState(false);
    // const { isDark, toggleTheme } = useTheme();

    return (
        <div className="min-h-screen bg-black text-gray-200 font-sans selection:bg-red-500/30 selection:text-white">
            {/* Skip to Content Link for Accessibility */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-red-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold focus:uppercase focus:tracking-wider"
            >
                Saltar al contenido principal
            </a>

            <OnboardingTutorial
                user={user}
                profile={profile}
                isManualTrigger={manualTutorialTrigger}
                isOpen={manualTutorialTrigger}
                onClose={() => setManualTutorialTrigger(false)}
            />

            {/* Suspension Banner - Shows when account is suspended */}
            {profile?.accountStatus === 'suspended' && (
                <SuspensionBanner
                    suspensionInfo={profile.suspensionInfo}
                    accountStatus={profile.accountStatus}
                />
            )}

            {/* Mobile Header */}
            <div className="md:hidden fixed top-0 w-full bg-black/95 backdrop-blur-xl border-b border-neutral-900 z-50 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <div className="bg-red-600 p-1 rounded-lg shadow-[0_0_10px_rgba(220,38,38,0.5)] -rotate-12 overflow-hidden transform-gpu border border-red-600">
                        <Dumbbell size={16} className="text-white rotate-45" strokeWidth={2.5} />
                    </div>
                    <span className="font-black text-white tracking-tighter text-lg italic">
                        GYM TRACKER <span className="text-red-600">PRO</span>
                    </span>
                </div>

                <div className="flex items-center gap-1">
                    {/* Theme Toggle - Disabled per user request
                    <button
                        onClick={toggleTheme}
                        className="p-2 text-neutral-400 hover:text-white transition-colors"
                        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {isDark ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                    */}
                    <button
                        onClick={() => setManualTutorialTrigger(true)}
                        className="p-2 text-neutral-400 hover:text-white transition-colors"
                        aria-label="Help"
                    >
                        <HelpCircle size={20} />
                    </button>
                    <LanguageSwitcher />
                    {/* Mobile User Avatar - Clickable to go to profile */}
                    <button
                        onClick={() => onNavigate('profile')}
                        className="w-8 h-8 rounded-full bg-neutral-800 border-2 border-neutral-700 overflow-hidden flex items-center justify-center relative hover:border-red-500 transition-colors"
                        aria-label="Go to profile"
                    >
                        {(profile?.profilePicture || user?.photoURL) ? (
                            <img
                                src={profile?.profilePicture || user?.photoURL}
                                alt="Profile"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="font-black text-white text-xs">
                                {profile?.displayName?.charAt(0).toUpperCase() || user?.displayName?.charAt(0).toUpperCase() || 'U'}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Desktop Sidebar */}
            <Sidebar
                user={user}
                profile={profile}
                role={role}
                activeTab={activeTab}
                onNavigate={onNavigate}
                onLogout={onLogout}
                isProfileComplete={isProfileComplete}
                onShowTutorial={() => setManualTutorialTrigger(true)}
            />

            {/* Main Content Area */}
            <main
                id="main-content"
                className="
        min-h-screen
        pt-16 pb-24 md:pt-0 md:pb-0 /* Mobile padding increased to prevent overlap */
        md:pl-64 /* Desktop sidebar offset */
        transition-all duration-300
      ">
                <div className="max-w-7xl mx-auto p-4 md:p-8">
                    {children}
                </div>
            </main>

            {/* Mobile Bottom Navigation */}
            <BottomNav
                role={role}
                activeTab={activeTab}
                onNavigate={onNavigate}
                isProfileComplete={isProfileComplete}
            />

            {/* Global Overlays */}
            <AchievementModalManager />
            <PWAInstallBanner />
        </div>
    );
};

export default Layout;
